import React, { useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Button, Table, Space, Typography, Tag, Tooltip, Input, Modal, message, Breadcrumb, Result, Empty } from 'antd';
import { ArrowLeftOutlined, PlusOutlined, DeleteOutlined, EditOutlined, SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import { INIT_SALE_DATA, SALE_PACKAGES, groupAppKeys } from './mockData';
import { findCategory, collectCategoryIds, ALL_KEY, NONE_KEY } from './components/categoryTreeUtils';
import SaleCategoryTree from './components/SaleCategoryTree';
import AddSalePackageDialog from './components/AddSalePackageDialog';
import EditSalePackageDialog from './components/EditSalePackageDialog';
import { DomesticPrice, OverseasPrice, CostPrice } from '../components/PackagePriceCells';

const { Text } = Typography;

const getDirectionText = (d) => (d === 1 ? '设备' : '用户');

// ══════════════════════════════════════════════
//  自定义售卖 - 功能入口套餐配置（左侧三级分类树 + 右侧套餐表格）
// ══════════════════════════════════════════════

const CustomSaleConfig = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const entryId = searchParams.get('id');
    const entId = searchParams.get('ent') || 'ENT001';

    const sourceEntry = useMemo(
        () => (INIT_SALE_DATA[entId]?.entries || []).find((e) => e.id === entryId),
        [entryId, entId],
    );

    const [categories, setCategories] = useState(() => JSON.parse(JSON.stringify(sourceEntry?.categories || [])));
    const [packages, setPackages] = useState(() => JSON.parse(JSON.stringify(sourceEntry?.packages || [])));
    const [selectedKey, setSelectedKey] = useState(ALL_KEY);
    const [addDialog, setAddDialog] = useState(false);
    const [editDialog, setEditDialog] = useState({ open: false, row: null });
    const [keyword, setKeyword] = useState({ name: '', packageId: '' });
    const [appliedKeyword, setAppliedKeyword] = useState({ name: '', packageId: '' });

    const sourceMap = useMemo(() => {
        const map = {};
        SALE_PACKAGES.forEach((p) => { map[p.packageId] = p; });
        return map;
    }, []);

    if (!sourceEntry) {
        return (
            <div style={{ padding: 24 }}>
                <Result status="404" title="功能入口不存在" extra={<Button type="primary" onClick={() => navigate('/operation/custom-sale')}>返回列表</Button>} />
            </div>
        );
    }

    const selectedNode = (selectedKey === NONE_KEY || selectedKey === ALL_KEY) ? null : findCategory(categories, selectedKey);
    const selectedIds = selectedNode ? collectCategoryIds(selectedNode) : [];
    const visiblePackages = packages.filter((p) => {
        if (selectedKey === ALL_KEY) return true;
        if (selectedKey === NONE_KEY) return !p.categoryId;
        return selectedIds.includes(p.categoryId);
    });
    const queriedPackages = visiblePackages.filter((p) => {
        const pkg = sourceMap[p.packageId] || {};
        if (appliedKeyword.name.trim() && !(pkg.name || '').toLowerCase().includes(appliedKeyword.name.trim().toLowerCase())) return false;
        if (appliedKeyword.packageId.trim() && !p.packageId.toLowerCase().includes(appliedKeyword.packageId.trim().toLowerCase())) return false;
        return true;
    });
    const handleQuery = () => setAppliedKeyword(keyword);
    const handleReset = () => { setKeyword({ name: '', packageId: '' }); setAppliedKeyword({ name: '', packageId: '' }); };
    const selectedTitle = selectedKey === ALL_KEY ? '所有分类' : selectedKey === NONE_KEY ? '未分类' : (selectedNode?.name || '未分类');

    const updatePackage = (id, patch) => {
        setPackages((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    };

    const handleEditSave = (id, patch) => {
        updatePackage(id, patch);
        setEditDialog({ open: false, row: null });
        message.success('保存成功');
    };

    const handleRemove = (row) => {
        Modal.confirm({
            title: '移除确认',
            content: `确定将套餐「${sourceMap[row.packageId]?.name || row.packageId}」从本入口移除？`,
            okText: '确定', cancelText: '取消', okButtonProps: { danger: true },
            onOk: () => {
                setPackages((prev) => prev.filter((p) => p.id !== row.id));
                message.success('移除成功');
            },
        });
    };

    const handleAddConfirm = (rows) => {
        const created = rows.map((row) => ({
            id: `sp_${Date.now()}_${row.packageId}`,
            packageId: row.packageId,
            categoryId: row.categoryId || null,
            upApps: row.upApps,
            domestic: row.domestic,
            overseas: row.overseas,
        }));
        setPackages((prev) => [...prev, ...created]);
        setAddDialog(false);
        message.success(`已添加 ${created.length} 个套餐`);
    };

    const subscriptionCell = (pkg) => {
        if (pkg.isFree === 1) return <Tag color="green">免费套餐</Tag>;
        if (pkg.isSubscription === 1 || pkg.isNoSubscription === 1) {
            return (
                <Space size={4} wrap>
                    {pkg.isSubscription === 1 && <Tag color="blue">订阅制</Tag>}
                    {pkg.isNoSubscription === 1 && <Tag color="orange">非订阅</Tag>}
                </Space>
            );
        }
        return '-';
    };

    const columns = [
        {
            title: '套餐信息', key: 'package', width: 200, fixed: 'left',
            render: (_, row) => {
                const pkg = sourceMap[row.packageId] || {};
                return (
                    <div>
                        <div style={{ fontWeight: 600 }}>{pkg.name || row.packageId}</div>
                        <span style={{ fontSize: 12, color: '#909399', fontFamily: 'monospace' }}>{row.packageId}</span>
                    </div>
                );
            },
        },
        { title: '应用方向', key: 'direction', width: 90, align: 'center', render: (_, row) => getDirectionText(sourceMap[row.packageId]?.applicationDirection) },
        {
            title: '上架 App', key: 'upApps', width: 220,
            render: (_, row) => {
                const groups = groupAppKeys(row.upApps);
                if (!groups.length) return '-';
                return (
                    <Space size={4} wrap>
                        {groups.map((g) => (
                            <Tooltip key={g.appName} title={g.regions.length ? g.regions.join('、') : '已选择该 App 下区域'}>
                                <Tag color="green">{g.appName}{g.regions.length ? `（${g.regions.length}）` : ''}</Tag>
                            </Tooltip>
                        ))}
                    </Space>
                );
            },
        },
        {
            title: '售卖区域', key: 'sellRegion', width: 140,
            render: (_, row) => {
                if (!row.domestic && !row.overseas) return '-';
                return (
                    <Space size={4} wrap>
                        {row.domestic && <Tag color="blue">国内</Tag>}
                        {row.overseas && <Tag color="purple">海外</Tag>}
                    </Space>
                );
            },
        },
        { title: '订阅类型', key: 'subscription', width: 150, align: 'center', render: (_, row) => subscriptionCell(sourceMap[row.packageId] || {}) },
        { title: '国内售价', key: 'cny', width: 110, render: (_, row) => <DomesticPrice pkg={sourceMap[row.packageId] || {}} /> },
        { title: '海外售价', key: 'usd', width: 180, render: (_, row) => <OverseasPrice pkg={sourceMap[row.packageId] || {}} /> },
        { title: '套餐底价', key: 'cost', width: 110, render: (_, row) => <CostPrice pkg={sourceMap[row.packageId] || {}} /> },
        {
            title: '归属分类', key: 'category', width: 140,
            render: (_, row) => (row.categoryId ? (findCategory(categories, row.categoryId)?.name || '-') : <Text type="secondary">未分类</Text>),
        },
        {
            title: '操作', key: 'action', width: 140, align: 'center', fixed: 'right',
            render: (_, row) => (
                <Space size={8}>
                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => setEditDialog({ open: true, row })}>编辑</Button>
                    <Button type="link" size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemove(row)}>移除</Button>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24, background: '#f5f6f8', minHeight: '100vh' }}>
            <Breadcrumb style={{ marginBottom: 16 }} items={[
                { title: '运营管理' },
                { title: '套餐售卖' },
                { title: <Button type="link" size="small" style={{ padding: 0 }} onClick={() => navigate('/operation/custom-sale')}>自定义售卖</Button> },
                { title: sourceEntry.name },
            ]} />

            <Card>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
                    <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/operation/custom-sale')}>返回</Button>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 16, fontWeight: 600 }}>{sourceEntry.name}</div>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            编码 {sourceEntry.code} · 当前入口共 <Text style={{ color: '#1677ff', fontWeight: 600 }}>{packages.length}</Text> 个套餐
                        </Text>
                    </div>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddDialog(true)}>添加套餐</Button>
                </div>

                <div style={{ display: 'flex', gap: 16, alignItems: 'stretch' }}>
                    <div style={{ width: 260, flexShrink: 0, borderRight: '1px solid #f0f0f0', paddingRight: 16 }}>
                        <SaleCategoryTree
                            treeData={categories}
                            packages={packages}
                            selectedKey={selectedKey}
                            onSelect={setSelectedKey}
                            onChange={setCategories}
                        />
                    </div>

                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
                            <Space>
                                <Input
                                    size="small" placeholder="套餐名称" allowClear prefix={<SearchOutlined />} style={{ width: 160 }}
                                    value={keyword.name} onChange={(e) => setKeyword((k) => ({ ...k, name: e.target.value }))}
                                    onPressEnter={handleQuery}
                                />
                                <Input
                                    size="small" placeholder="套餐ID" allowClear prefix={<SearchOutlined />} style={{ width: 160 }}
                                    value={keyword.packageId} onChange={(e) => setKeyword((k) => ({ ...k, packageId: e.target.value }))}
                                    onPressEnter={handleQuery}
                                />
                                <Button type="primary" size="small" icon={<SearchOutlined />} onClick={handleQuery}>查询</Button>
                                <Button size="small" icon={<ReloadOutlined />} onClick={handleReset}>重置</Button>
                            </Space>
                            <Space>
                                <span style={{ fontSize: 15, fontWeight: 600 }}>{selectedTitle}</span>
                                <Text type="secondary" style={{ fontSize: 12 }}>{queriedPackages.length} 个套餐</Text>
                            </Space>
                        </div>
                        <Table
                            dataSource={queriedPackages}
                            columns={columns}
                            rowKey="id"
                            bordered={false}
                            size="small"
                            scroll={{ x: 1340 }}
                            pagination={false}
                            locale={{ emptyText: <Empty description="该分类下暂无套餐" /> }}
                        />
                    </div>
                </div>
            </Card>

            <AddSalePackageDialog
                open={addDialog}
                sourcePackages={SALE_PACKAGES}
                categories={categories}
                existingIds={packages.map((p) => p.packageId)}
                onCancel={() => setAddDialog(false)}
                onConfirm={handleAddConfirm}
            />

            <EditSalePackageDialog
                open={editDialog.open}
                row={editDialog.row}
                pkgName={sourceMap[editDialog.row?.packageId]?.name}
                categories={categories}
                onCancel={() => setEditDialog({ open: false, row: null })}
                onSave={handleEditSave}
            />
        </div>
    );
};

export default CustomSaleConfig;
